/**
 * Core WebSocket hook for real-time communication with the RealtimeHub DO.
 *
 * Features:
 * - Auto-reconnect with exponential backoff (1s → 30s) + jitter
 * - Heartbeat: reconnect if no message in 45s
 * - Deduplication via rolling Set of last 200 event IDs
 * - lastEventId tracking per channel for replay on reconnect
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getAuthToken } from '~/contexts/AuthContext';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8787';
const HEARTBEAT_TIMEOUT_MS = 45_000;
const MIN_RECONNECT_MS = 1_000;
const MAX_RECONNECT_MS = 30_000;
const MAX_DEDUP_IDS = 200;

export type WSChannel = 'signals' | 'messages';

export interface WSEvent {
  channel: string;
  type: string;
  id: string;
  data: Record<string, unknown>;
}

interface UseWebSocketOptions {
  channels: WSChannel[];
  onEvent?: (event: WSEvent) => void;
  enabled?: boolean;
}

interface WSState {
  connected: boolean;
  error: string | null;
  reconnecting: boolean;
}

export function useWebSocket(options: UseWebSocketOptions) {
  const { channels, onEvent, enabled = true } = options;
  const [state, setState] = useState<WSState>({
    connected: false,
    error: null,
    reconnecting: false,
  });

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectDelayRef = useRef(MIN_RECONNECT_MS);
  const seenIdsRef = useRef<Set<string>>(new Set());
  const lastEventIdRef = useRef<Record<string, string>>({});
  const mountedRef = useRef(true);
  const channelsRef = useRef(channels);
  channelsRef.current = channels;
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const clearTimers = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current);
      heartbeatTimeoutRef.current = null;
    }
  }, []);

  const resetHeartbeat = useCallback(() => {
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current);
    }
    heartbeatTimeoutRef.current = setTimeout(() => {
      console.warn('[WS] Heartbeat timeout — reconnecting');
      if (wsRef.current) {
        wsRef.current.close(4000, 'Heartbeat timeout');
      }
    }, HEARTBEAT_TIMEOUT_MS);
  }, []);

  const addSeenId = useCallback((id: string) => {
    const seen = seenIdsRef.current;
    if (seen.size >= MAX_DEDUP_IDS) {
      // Evict oldest 50 entries (Set preserves insertion order)
      const arr = Array.from(seen);
      seenIdsRef.current = new Set(arr.slice(50));
    }
    seenIdsRef.current.add(id);
  }, []);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) {
      wsRef.current.close();
    }
    clearTimers();

    // Auth: prefer cookie (sent automatically with the WS handshake on
    // same-registrable-domain origins). Fall back to ?token= for callers
    // that still have a localStorage JWT — covers the legacy cross-site
    // setup before the kingsdomain.trade rename.
    const token = getAuthToken();

    const wsProtocol = API_BASE.startsWith('https') ? 'wss' : 'ws';
    const wsHost = API_BASE.replace(/^https?:\/\//, '');
    const params = new URLSearchParams();
    if (token) params.set('token', token);

    const lastId = lastEventIdRef.current['signals'] || lastEventIdRef.current['messages'] || '';
    if (lastId) params.set('lastEventId', lastId);

    const qs = params.toString();
    const url = `${wsProtocol}://${wsHost}/api/ws${qs ? `?${qs}` : ''}`;
    console.log('[WS] Connecting to', url.replace(/token=[^&]+/, 'token=***'));

    const socket = new WebSocket(url);
    wsRef.current = socket;

    socket.onopen = () => {
      if (!mountedRef.current) return;
      console.log('[WS] Connected');
      setState({ connected: true, error: null, reconnecting: false });
      reconnectDelayRef.current = MIN_RECONNECT_MS;
      resetHeartbeat();

      // Subscribe to channels with lastEventId for replay
      const lastEventId = lastEventIdRef.current['signals'] || lastEventIdRef.current['messages'] || '';
      socket.send(JSON.stringify({
        type: 'subscribe',
        channels: channelsRef.current,
        lastEventId: lastEventId || undefined,
      }));
    };

    socket.onmessage = (event) => {
      if (!mountedRef.current) return;
      resetHeartbeat();

      try {
        const parsed: WSEvent & { type: string } = JSON.parse(event.data);

        // Control messages
        if (parsed.type === 'connected' || parsed.type === 'subscribed' || parsed.type === 'pong' || parsed.type === 'replay_complete') {
          return;
        }

        if (parsed.type === 'error') {
          console.error('[WS] Server error:', parsed);
          return;
        }

        // Data messages — deduplicate
        if (parsed.id && seenIdsRef.current.has(parsed.id)) {
          return;
        }
        if (parsed.id) {
          addSeenId(parsed.id);
        }

        // Track lastEventId per channel
        if (parsed.channel === 'signals' && parsed.data?.created_at) {
          lastEventIdRef.current['signals'] = String(parsed.data.created_at);
        }
        if (parsed.channel === 'messages' && parsed.data?.id) {
          lastEventIdRef.current['messages'] = String(parsed.data.id);
        }

        onEventRef.current?.(parsed);
      } catch (e) {
        console.error('[WS] Parse error:', e);
      }
    };

    socket.onclose = (event) => {
      if (!mountedRef.current) return;
      console.log(`[WS] Closed: code=${event.code} reason=${event.reason}`);
      wsRef.current = null;

      if (heartbeatTimeoutRef.current) {
        clearTimeout(heartbeatTimeoutRef.current);
      }

      setState(prev => ({
        ...prev,
        connected: false,
        error: event.code === 1000 ? null : 'Connection lost',
        reconnecting: event.code !== 1000,
      }));

      // Don't reconnect on normal close
      if (event.code === 1000) return;

      // Exponential backoff with jitter
      const delay = reconnectDelayRef.current + Math.random() * 1000;
      console.log(`[WS] Reconnecting in ${Math.round(delay)}ms`);
      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectDelayRef.current = Math.min(reconnectDelayRef.current * 2, MAX_RECONNECT_MS);
        connect();
      }, delay);
    };

    socket.onerror = () => {
      // onclose will fire after onerror, so we just log here
      console.error('[WS] Error event');
    };
  }, [clearTimers, resetHeartbeat, addSeenId]);

  const disconnect = useCallback(() => {
    clearTimers();
    if (wsRef.current) {
      wsRef.current.close(1000, 'Client disconnect');
      wsRef.current = null;
    }
    setState({ connected: false, error: null, reconnecting: false });
  }, [clearTimers]);

  useEffect(() => {
    mountedRef.current = true;

    if (enabled) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      mountedRef.current = false;
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  return {
    ...state,
    connect,
    disconnect,
  };
}
